import { cn, relativeTime } from "@/lib/utils";
import {
	ArrowDownLeft,
	ArrowUpRight,
	ChevronDown,
	ChevronRight,
	Mail,
	Shield,
} from "lucide-react";
import { useState } from "react";

// ============================================================
// Shared Email Thread Components
// Used by: BookingEmailsTab, EmailsPage
// ============================================================

export interface EmailEntry {
	id: string;
	gmailMessageId: string | null;
	gmailThreadId: string | null;
	subject: string | null;
	fromEmail: string | null;
	toEmail: string | null;
	direction: string | null;
	classified: string | null;
	confidence: number | null;
	snippet: string | null;
	bodyText?: string | null;
	bodyHtml?: string | null;
	gmailDate: string | null;
	processedAt: string | null;
}

export interface EmailThread {
	threadId: string;
	subject: string;
	emails: EmailEntry[];
	latestDate: string;
}

export function groupByThread(emails: EmailEntry[]): EmailThread[] {
	const threads = new Map<string, EmailEntry[]>();

	for (const email of emails) {
		const key = email.gmailThreadId ?? email.id;
		const existing = threads.get(key) ?? [];
		existing.push(email);
		threads.set(key, existing);
	}

	return Array.from(threads.entries())
		.map(([threadId, emails]) => {
			const sorted = [...emails].sort((a, b) => {
				const aTime = a.gmailDate ?? a.processedAt ?? "";
				const bTime = b.gmailDate ?? b.processedAt ?? "";
				return aTime.localeCompare(bTime);
			});

			return {
				threadId,
				subject: sorted[0]?.subject ?? "(Kein Betreff)",
				emails: sorted,
				latestDate:
					sorted[sorted.length - 1]?.gmailDate ??
					sorted[sorted.length - 1]?.processedAt ??
					"",
			};
		})
		.sort((a, b) => b.latestDate.localeCompare(a.latestDate));
}

// ============================================================
// Thread Card — Expandable thread header
// ============================================================

export function ThreadCard({
	thread,
	children,
	defaultExpanded,
	onExpand,
}: {
	thread: EmailThread;
	children?: React.ReactNode;
	defaultExpanded?: boolean;
	onExpand?: () => void;
}) {
	const [expanded, setExpanded] = useState(
		defaultExpanded ?? thread.emails.length === 1,
	);

	function toggle() {
		const next = !expanded;
		setExpanded(next);
		if (next && onExpand) onExpand();
	}

	return (
		<div className="card !p-0 overflow-hidden">
			<button
				type="button"
				onClick={toggle}
				className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-stone-50 transition-colors"
			>
				<div className="shrink-0 text-stone-400">
					{expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
				</div>
				<div className="flex-1 min-w-0">
					<p className="text-sm font-medium text-stone-800 truncate">
						{thread.subject}
					</p>
					<div className="flex items-center gap-2 text-xs text-stone-500 mt-0.5">
						<span>
							{thread.emails.length} Nachricht
							{thread.emails.length !== 1 ? "en" : ""}
						</span>
						<span>{relativeTime(thread.latestDate)}</span>
					</div>
				</div>
				<DirectionIndicators emails={thread.emails} />
			</button>

			{expanded && <div className="border-t border-stone-100">{children}</div>}
		</div>
	);
}

// ============================================================
// Direction Indicators — in/out dots for thread preview
// ============================================================

export function DirectionIndicators({ emails }: { emails: EmailEntry[] }) {
	return (
		<div className="flex gap-1 shrink-0">
			{emails.slice(-5).map((e) => (
				<div
					key={e.id}
					className={cn(
						"w-2 h-2 rounded-full",
						e.direction === "inbox" ? "bg-blue-400" : "bg-green-400",
					)}
					title={
						e.direction === "inbox"
							? `Eingang: ${e.fromEmail}`
							: `Gesendet: ${e.toEmail}`
					}
				/>
			))}
		</div>
	);
}

// ============================================================
// Email Card — Single email with expandable body
// ============================================================

export function EmailCard({
	email,
	isLast,
	extra,
}: { email: EmailEntry; isLast: boolean; extra?: React.ReactNode }) {
	const [showBody, setShowBody] = useState(false);
	const isInbound = email.direction === "inbox";
	const hasBody = Boolean(email.bodyText || email.bodyHtml);

	return (
		<div className={cn("px-4 py-3", !isLast && "border-b border-stone-50")}>
			<div className="flex items-start gap-3">
				<div
					className={cn(
						"mt-0.5 shrink-0 rounded-full p-1.5",
						isInbound
							? "bg-blue-50 text-blue-600"
							: "bg-green-50 text-green-600",
					)}
				>
					{isInbound ? <ArrowDownLeft size={12} /> : <ArrowUpRight size={12} />}
				</div>
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2 flex-wrap">
						<span className="text-sm font-medium text-stone-700">
							{isInbound ? (email.fromEmail ?? "—") : (email.toEmail ?? "—")}
						</span>
						{email.classified && (
							<ClassificationBadge
								type={email.classified}
								confidence={email.confidence}
							/>
						)}
					</div>
					<p className="text-xs text-stone-400 mt-0.5">
						{email.gmailDate
							? new Date(email.gmailDate).toLocaleString("de-DE", {
									day: "2-digit",
									month: "2-digit",
									year: "numeric",
									hour: "2-digit",
									minute: "2-digit",
								})
							: email.processedAt
								? relativeTime(email.processedAt)
								: "—"}
					</p>

					{email.snippet && !showBody && (
						<p className="text-xs text-stone-500 mt-1.5 line-clamp-2">
							{email.snippet}
						</p>
					)}

					{showBody && hasBody && (
						<div className="mt-2 text-sm text-stone-600 bg-stone-50 rounded-md p-3 max-h-80 overflow-y-auto">
							{email.bodyText ? (
								<pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed">
									{email.bodyText}
								</pre>
							) : email.bodyHtml ? (
								<pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed">
									{stripHtml(email.bodyHtml)}
								</pre>
							) : null}
						</div>
					)}

					{hasBody && (
						<button
							type="button"
							onClick={() => setShowBody(!showBody)}
							className="text-xs text-brand-600 hover:text-brand-700 mt-1.5 flex items-center gap-1"
						>
							<Mail size={10} />
							{showBody ? "Weniger anzeigen" : "E-Mail anzeigen"}
						</button>
					)}

					{extra}
				</div>
			</div>
		</div>
	);
}

// ============================================================
// Classification Badge
// ============================================================

export function ClassificationBadge({
	type,
	confidence,
}: { type: string; confidence: number | null }) {
	const colors: Record<string, string> = {
		booking: "bg-blue-50 text-blue-700",
		angebot: "bg-amber-50 text-amber-700",
		ab: "bg-emerald-50 text-emerald-700",
		rechnung: "bg-orange-50 text-orange-700",
		stornierung: "bg-red-50 text-red-700",
		"follow-up": "bg-purple-50 text-purple-700",
		irrelevant: "bg-stone-100 text-stone-500",
	};

	return (
		<span
			className={cn(
				"badge text-xs flex items-center gap-1",
				colors[type] ?? "bg-stone-100 text-stone-500",
			)}
		>
			{confidence != null && confidence >= 0.8 && (
				<Shield size={8} className="opacity-60" />
			)}
			{type}
			{confidence != null && (
				<span className="opacity-50">{Math.round(confidence * 100)}%</span>
			)}
		</span>
	);
}

// ============================================================
// Helper: Strip HTML tags
// ============================================================

export function stripHtml(html: string): string {
	return html
		.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
		.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
		.replace(/<[^>]+>/g, " ")
		.replace(/&nbsp;/g, " ")
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/\s+/g, " ")
		.trim();
}
